import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { LoyaltyProgramFacade } from '@features/loyalty-program/facades/loyalty-program.facade';
import { CmsModule } from '@rd/cms/cms.module';
import { CmsFacade } from '@rd/cms/facades/cms.facade';
import { ENV } from '@rd/core/tokens/rd-lib.token';
import type { MultipleFeaturedPartnersV2 } from '@rd/types';
import { isNotNull } from '@rd/utils/isNotNull';
import { EnvironmentType } from 'global-types';
import { filter, map, Observable, switchMap } from 'rxjs';
import { BnzFeaturedPartnerV2BannerComponent } from './featured-partner-v2-banner.component';
import { BnzFeaturedPartnerV2CarouselComponent } from './featured-partner-v2-carousel.component';

const MAX_FEATURED_PARTNERS = 5;

@Component({
  selector: 'bnz-featured-partner-v2',
  template: `
    <ng-container *ngIf="featuredPartnerData$ | async as data">
      <div class="overflow-x-clip lg:px-4">
        <div
          *ngIf="data && data.items.length > 0"
          [ngClass]="[
            'lg:screen-max-size mx-4 pt-8 mb-4',
            'border-0 lg:mx-auto lg:pt-16 lg:-mb-4'
          ]">
          <ng-container *ngIf="data.items.length === 1">
            <h2 *ngIf="data.heading" class="typo-heading-2-desktop m-0 mb-8 lg:mb-6 text-[#002F6B] leading-[30px] lg:leading-[40px]">
              {{ data.heading }}
            </h2>
            <bnz-featured-partner-v2-banner
              [banner]="data.items[0]"
            />
          </ng-container>
          <bnz-featured-partner-v2-carousel
            *ngIf="data.items.length > 1"
            [heading]="data.heading"
            [banners]="data.items"
          />
        </div>
      </div>
    </ng-container>
  `,
  imports: [
    CmsModule,
    CommonModule,
    BnzFeaturedPartnerV2BannerComponent,
    BnzFeaturedPartnerV2CarouselComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BnzFeaturedPartnerV2Component {
  private cmsFacade = inject(CmsFacade);
  private loyaltyProgramFacade = inject(LoyaltyProgramFacade);
  private env: EnvironmentType = inject(ENV);

  featuredPartnerData$: Observable<MultipleFeaturedPartnersV2> = this.cmsFacade
    .getContentByName({
      pageName: 'featured-partners-update-v2',
      pageType: 'feature_partner_v2',
      locale: 'en-au',
    })
    .pipe(
      filter(
        (content): content is MultipleFeaturedPartnersV2 =>
          isNotNull(content) && content.type === 'feature_partner_v2',
      ),
      map((data) => ({
        ...data,
        items: data.items.filter((item) => {
          const isVisible =
            this.env === 'production'
              ? item.showFeaturedPartner
              : item.showFeaturedPartner || item.previewFeaturedPartner;
          return (
            isVisible &&
            // requirements for showing banner following ticket RD-5684
            !!item.desktopImage &&
            !!item.mobileImage &&
            !!item.title &&
            !!item.description
          );
        }),
      })),
      switchMap((data) =>
        this.loyaltyProgramFacade.filteredPointsTransferLoyaltyPrograms$.pipe(
          map((loyaltyPrograms) => ({
            ...data,
            items: data.items
              .map((item) => {
                const loyaltyProgram = loyaltyPrograms.find(
                  (program) =>
                    program.transferConnectIdentifier ===
                    item.partnerQueryString,
                );

                if (!loyaltyProgram) {
                  return null;
                }

                return {
                  ...item,
                  ctaText: item.ctaText ?? 'Transfer to travel partners',
                  ctaUrl:
                    item.ctaUrl ?? `/transfer/checkout/${loyaltyProgram.id}`,
                };
              })
              .filter(isNotNull)
              .slice(0, MAX_FEATURED_PARTNERS),
          })),
        ),
      ),
    );
}
